<script lang="ts">
  import { CATEGORIES, type CheckReport, type Finding, type PreviewPara, type Severity } from '@gostword/engine';
  import { tick } from 'svelte';
  import { mailtoLink } from '../lib/config';
  import { inTelegram } from '../lib/telegram';
  import type { FixRequest, FixResponse } from '../lib/engine';
  import { countOf, excerpt, fmtDuration, SEVERITY_PLURAL, SEVERITY_SHORT } from '../lib/format';
  import DocView from './DocView.svelte';
  import FixDialog from './FixDialog.svelte';
  import Icon from './Icon.svelte';

  let {
    report,
    fileName,
    requests,
    elapsed,
    onReset,
    onFix,
    onProfile,
  }: {
    report: CheckReport;
    fileName: string;
    requests: number;
    elapsed: number;
    onReset: () => void;
    onFix: (r: FixRequest) => Promise<FixResponse>;
    onProfile: () => void;
  } = $props();

  const SEVS: Severity[] = ['error', 'warning', 'info'];
  const RANK: Record<Severity, number> = { error: 3, warning: 2, info: 1 };
  const PAGE = 40;

  let filter = $state<Severity | 'all'>('all');
  let open = $state<Record<string, boolean>>({});
  let shown = $state<Record<string, number>>({});
  let selectedId = $state<string | null>(null);
  let selectedPara = $state<number | null>(null);
  let fixOpen = $state(false);
  let tab = $state<'issues' | 'doc'>('issues');

  // Правила с ошибками раскрыты сразу, остальные — по клику.
  $effect.pre(() => {
    const initial: Record<string, boolean> = {};
    for (const r of report.rules) if (r.status === 'fail' && r.severity === 'error') initial[r.id] = true;
    open = initial;
  });

  const paraText = $derived.by(() => {
    const m = new Map<number, string>();
    const add = (p: PreviewPara): void => void m.set(p.i, p.text);
    for (const b of report.preview.blocks) {
      if (b.kind === 'p') add(b);
      else for (const row of b.rows) for (const cell of row) for (const p of cell) add(p);
    }
    return m;
  });

  const byRule = $derived.by(() => {
    const m = new Map<string, Finding[]>();
    for (const f of report.findings) {
      const list = m.get(f.rule);
      if (list) list.push(f);
      else m.set(f.rule, [f]);
    }
    return m;
  });

  const byPara = $derived.by(() => {
    const m = new Map<number, Finding[]>();
    for (const f of report.findings) {
      for (const i of f.paragraphs ?? (f.paragraph === undefined ? [] : [f.paragraph])) {
        const list = m.get(i);
        if (list) list.push(f);
        else m.set(i, [f]);
      }
    }
    return m;
  });

  const general = $derived(report.findings.filter((f) => f.paragraph === undefined));

  const groups = $derived(
    CATEGORIES.map((c) => ({
      ...c,
      rules: report.rules
        .filter((r) => r.category === c.id && r.status === 'fail')
        .map((rule) => ({ rule, findings: (byRule.get(rule.id) ?? []).filter((f) => filter === 'all' || f.severity === filter) }))
        .filter((g) => g.findings.length > 0),
    })).filter((c) => c.rules.length > 0),
  );

  const passed = $derived(report.rules.filter((r) => r.status === 'pass'));

  /** Письмо о неверном замечании: только сведения о правиле, без текста документа. */
  function reportLink(f: Finding): string {
    const rule = report.rules.find((r) => r.id === f.rule);
    const s = report.stats;
    return mailtoLink(`gostword: неверное замечание (${f.rule})`, [
      `Замечание: ${f.message}`,
      `Проверка: ${rule?.title ?? f.rule} (${f.rule})`,
      `Требования: ${report.profile.name}`,
      `Документ: ${s.kindTitle}, страниц: ${s.pages}`,
      '',
      'Что не так (например, «это не заголовок» или «в методичке другое требование»):',
      '',
      '',
      'Если можно, вставьте фрагмент текста, к которому относится замечание. Сам документ не прикладывайте: в нём ваши персональные данные.',
    ].join('\n'));
  }

  const statsLine = $derived.by(() => {
    const s = report.stats;
    return [
      s.kind !== 'unknown' ? s.kindTitle : '',
      `${countOf(s.pages, ['страница', 'страницы', 'страниц'])}${s.pagesExact ? '' : ' (примерно)'}`,
      countOf(s.words, ['слово', 'слова', 'слов']),
      s.figures ? countOf(s.figures, ['рисунок', 'рисунка', 'рисунков']) : '',
      s.tables ? countOf(s.tables, ['таблица', 'таблицы', 'таблиц']) : '',
      s.sources ? countOf(s.sources, ['источник', 'источника', 'источников']) : '',
    ]
      .filter(Boolean)
      .join(' · ');
  });

  function worst(list: Finding[]): Severity {
    let best: Severity = 'info';
    for (const f of list) if (RANK[f.severity] > RANK[best]) best = f.severity;
    return best;
  }

  const narrow = (): boolean => matchMedia('(max-width: 900px)').matches;

  function flash(i: number): void {
    const el = document.querySelector<HTMLElement>(`.paper [data-i="${i}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.remove('flash');
    void el.offsetWidth;
    el.classList.add('flash');
  }

  async function selectFinding(f: Finding): Promise<void> {
    selectedId = f.id;
    if (f.paragraph === undefined) return;
    selectedPara = f.paragraph;
    if (narrow()) tab = 'doc';
    await tick();
    flash(f.paragraph);
  }

  async function selectPara(i: number): Promise<void> {
    const list = byPara.get(i);
    if (!list?.length) return;
    selectedPara = i;
    selectedId = list[0]!.id;
    filter = 'all';
    for (const f of list) open[f.rule] = true;
    if (narrow()) tab = 'issues';
    await tick();
    document.querySelector(`[data-finding="${CSS.escape(list[0]!.id)}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
</script>

<div class="report">
  <section class="head">
    <div class="file">
      <div class="file-badge"><Icon name="doc" size={22} /></div>
      <div class="file-meta">
        <h1 title={fileName}>{fileName}</h1>
        <p>{statsLine}</p>
      </div>
    </div>
    <div class="actions">
      <button class="btn" onclick={onReset}>Другой файл</button>
      <button class="btn btn-primary" onclick={() => (fixOpen = true)} disabled={!report.findings.length}>
        <Icon name="download" size={18} /> {inTelegram() ? 'Получить' : 'Скачать'} <span class="wide-only">исправленный</span>
      </button>
    </div>
  </section>

  <section class="summary" aria-label="Итоги проверки">
    {#each SEVS as sev (sev)}
      <button
        class="tile sev-{sev}"
        class:active={filter === sev}
        aria-pressed={filter === sev}
        onclick={() => (filter = filter === sev ? 'all' : sev)}
      >
        <span class="num">{report.summary[sev]}</span>
        <span class="lbl">
          <span class="dot"></span><span class="full">{SEVERITY_PLURAL[sev]}</span><span class="short">{SEVERITY_SHORT[sev]}</span>
        </span>
      </button>
    {/each}
    <div class="tile tile-fix">
      <span class="num">{report.summary.fixable}</span>
      <span class="lbl"><Icon name="edit" size={14} /> исправим автоматически</span>
    </div>
    <div class="tile tile-pass">
      <span class="num">{report.summary.passed}<small> из {report.summary.rules}</small></span>
      <span class="lbl"><Icon name="check" size={14} /> проверок пройдено</span>
    </div>
  </section>

  <p class="local">
    <Icon name="shield" size={16} />
    <span>Проверено на этом устройстве за {fmtDuration(elapsed)} · сетевых запросов во время проверки: {requests}</span>
    <span class="profile">· Требования: {report.profile.name} <button class="link" onclick={onProfile}>изменить</button></span>
  </p>

  <div class="tabs" role="tablist">
    <button role="tab" aria-selected={tab === 'issues'} class:active={tab === 'issues'} onclick={() => (tab = 'issues')}>
      Замечания · {report.findings.length}
    </button>
    <button role="tab" aria-selected={tab === 'doc'} class:active={tab === 'doc'} onclick={() => (tab = 'doc')}>Документ</button>
  </div>

  <div class="columns">
    <aside class="issues" class:hide={tab !== 'issues'} aria-label="Замечания">
      {#if !report.findings.length}
        <div class="clean">
          <div class="clean-icon"><Icon name="check" size={28} /></div>
          <h2>Замечаний нет</h2>
          <p>Документ прошёл все {countOf(report.summary.rules, ['проверку', 'проверки', 'проверок'])}.</p>
        </div>
      {/if}
      {#if filter !== 'all'}
        <p class="filter-note">
          Показаны только {SEVERITY_PLURAL[filter].toLowerCase()}. <button class="link" onclick={() => (filter = 'all')}>Показать все</button>
        </p>
      {/if}
      {#each groups as g (g.id)}
        <section class="cat">
          <h2>{g.title}</h2>
          {#each g.rules as { rule, findings } (rule.id)}
            {@const sev = worst(findings)}
            <div class="rule sev-{sev}" class:open={open[rule.id]}>
              <button class="rule-head" aria-expanded={!!open[rule.id]} onclick={() => (open[rule.id] = !open[rule.id])}>
                <span class="dot"></span>
                <span class="rule-title">{rule.title}</span>
                <span class="rule-count">{findings.length}</span>
                <span class="chev"><Icon name="chevron" size={16} /></span>
              </button>
              {#if open[rule.id]}
                <div class="rule-body">
                  {#if rule.why}
                    <p class="why">{rule.why}{#if rule.gost}<span class="gost">{rule.gost}</span>{/if}</p>
                  {/if}
                  <ul class="findings">
                    {#each findings.slice(0, shown[rule.id] ?? PAGE) as f (f.id)}
                      <li>
                        <button
                          class="finding sev-{f.severity}"
                          class:selected={selectedId === f.id}
                          data-finding={f.id}
                          onclick={() => selectFinding(f)}
                        >
                          <span class="msg">{f.message}</span>
                          {#if f.paragraph !== undefined}
                            <span class="quote">{excerpt(paraText.get(f.paragraph) ?? '', f.marks?.[0])}</span>
                          {/if}
                          {#if f.fix?.length}
                            <span class="fixable"><Icon name="edit" size={13} />{f.fixLabel ?? 'Исправим автоматически'}</span>
                          {/if}
                        </button>
                        {#if selectedId === f.id}
                          <a class="wrong" href={reportLink(f)}>Замечание неверное? Сообщите нам</a>
                        {/if}
                      </li>
                    {/each}
                  </ul>
                  {#if findings.length > (shown[rule.id] ?? PAGE)}
                    <button class="more" onclick={() => (shown[rule.id] = findings.length)}>
                      Показать ещё {findings.length - (shown[rule.id] ?? PAGE)}
                    </button>
                  {/if}
                </div>
              {/if}
            </div>
          {/each}
        </section>
      {/each}
      {#if passed.length}
        <details class="passed">
          <summary><Icon name="check" size={16} /> Без замечаний — {countOf(passed.length, ['проверка', 'проверки', 'проверок'])}</summary>
          <ul>
            {#each passed as r (r.id)}<li>{r.title}</li>{/each}
          </ul>
        </details>
      {/if}
    </aside>

    <section class="doc" class:hide={tab !== 'doc'} aria-label="Документ">
      {#if general.length}
        <div class="general">
          <h3>Ко всему документу</h3>
          <ul>
            {#each general as f (f.id)}
              <li class="sev-{f.severity}">
                <button class="general-item" onclick={() => { open[f.rule] = true; selectedId = f.id; tab = 'issues'; }}>
                  <span class="dot"></span><span>{f.message}</span>
                </button>
              </li>
            {/each}
          </ul>
        </div>
      {/if}
      <DocView blocks={report.preview.blocks} {byPara} {selectedPara} onSelect={selectPara} />
    </section>
  </div>
</div>

{#if fixOpen}
  <FixDialog {report} {onFix} onClose={() => (fixOpen = false)} />
{/if}

<style>
  .report {
    max-width: 1240px;
    margin: 0 auto;
    padding: 28px 24px 24px;
  }

  .head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
  }

  .file {
    display: flex;
    align-items: center;
    gap: 14px;
    min-width: 0;
  }

  .file-badge {
    width: 46px;
    height: 46px;
    flex: none;
    display: grid;
    place-items: center;
    border-radius: 13px;
    background: var(--accent-soft);
    color: var(--accent);
  }

  .file-meta {
    min-width: 0;
  }

  .file-meta h1 {
    max-width: min(640px, 70vw);
    overflow: hidden;
    font-size: 21px;
    font-weight: 660;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .file-meta p {
    margin-top: 3px;
    color: var(--text-2);
    font-size: 14px;
  }

  .actions {
    display: flex;
    gap: 10px;
  }

  .summary {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 12px;
    margin-top: 22px;
  }

  .tile {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 14px 16px;
    border: 1px solid var(--border);
    border-radius: var(--radius);
    background: var(--surface);
    box-shadow: var(--shadow-sm);
    text-align: left;
  }

  button.tile {
    cursor: pointer;
    transition:
      border-color 0.15s,
      box-shadow 0.15s;
  }

  button.tile:hover {
    border-color: var(--border-strong);
  }

  .tile.active {
    border-color: var(--sev);
    box-shadow: 0 0 0 3px var(--sev-soft);
  }

  .num {
    color: var(--sev, var(--text));
    font-size: 28px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.03em;
    line-height: 1.1;
  }

  .num small {
    margin-left: 5px;
    color: var(--text-3);
    font-size: 15px;
    font-weight: 500;
    letter-spacing: 0;
  }

  .tile-fix .num {
    color: var(--accent);
  }

  .tile-pass .num {
    color: var(--ok);
  }

  .lbl {
    display: flex;
    align-items: center;
    gap: 6px;
    color: var(--text-2);
    font-size: 13px;
  }

  .dot {
    width: 8px;
    height: 8px;
    flex: none;
    border-radius: 50%;
    background: var(--sev);
  }

  .local {
    margin: 16px 2px 0;
    color: var(--text-2);
    font-size: 13px;
  }

  .local > :global(svg) {
    margin-right: 4px;
    color: var(--ok);
    vertical-align: -3px;
  }

  .short {
    display: none;
  }

  .tabs {
    display: none;
  }

  .columns {
    display: grid;
    grid-template-columns: minmax(340px, 420px) minmax(0, 1fr);
    align-items: start;
    gap: 28px;
    margin-top: 20px;
  }

  .issues {
    position: sticky;
    top: 76px;
    max-height: calc(100vh - 92px);
    overflow: auto;
    padding: 2px 8px 24px 2px;
    scrollbar-width: thin;
  }

  .clean {
    padding: 28px 22px;
    border-radius: var(--radius);
    background: var(--ok-soft);
    text-align: center;
  }

  .clean-icon {
    width: 52px;
    height: 52px;
    margin: 0 auto 10px;
    display: grid;
    place-items: center;
    border-radius: 50%;
    background: var(--ok);
    color: #fff;
  }

  .clean p {
    margin-top: 6px;
    color: var(--text-2);
  }

  .filter-note {
    margin: 0 4px 12px;
    color: var(--text-2);
    font-size: 13px;
  }

  .cat + .cat {
    margin-top: 20px;
  }

  .cat h2 {
    margin: 0 4px 8px;
    color: var(--text-3);
    font-size: 12px;
    font-weight: 650;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .rule {
    margin-bottom: 8px;
    overflow: hidden;
    border: 1px solid var(--border);
    border-radius: 12px;
    background: var(--surface);
  }

  .rule-head {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 11px 12px 11px 14px;
    border: 0;
    background: none;
    cursor: pointer;
    text-align: left;
  }

  .rule-head:hover {
    background: var(--surface-2);
  }

  .rule-title {
    flex: 1;
    font-size: 14.5px;
    font-weight: 570;
  }

  .rule-count {
    padding: 1px 8px;
    border-radius: 999px;
    background: var(--sev-soft);
    color: var(--sev);
    font-size: 12.5px;
    font-weight: 680;
    font-variant-numeric: tabular-nums;
  }

  .chev {
    display: flex;
    color: var(--text-3);
    transition: transform 0.15s;
  }

  .rule.open .chev {
    transform: rotate(90deg);
  }

  .rule-body {
    padding: 10px 10px 12px;
    border-top: 1px solid var(--border);
  }

  .why {
    padding: 2px 4px 10px;
    color: var(--text-2);
    font-size: 13px;
  }

  .gost {
    display: inline-block;
    margin-left: 6px;
    padding: 0 6px;
    border-radius: 6px;
    background: var(--surface-3);
    color: var(--text-3);
    font-size: 11.5px;
    font-weight: 600;
    white-space: nowrap;
  }

  .findings {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .finding {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 9px 10px 9px 12px;
    border: 1px solid transparent;
    border-left: 3px solid var(--sev);
    border-radius: 9px;
    background: var(--surface-2);
    cursor: pointer;
    text-align: left;
  }

  .finding:hover {
    border-color: var(--border-strong);
    border-left-color: var(--sev);
  }

  .finding.selected {
    border-color: var(--sev);
    background: var(--sev-soft);
  }

  .msg {
    font-size: 13.5px;
    line-height: 1.4;
  }

  .quote {
    display: -webkit-box;
    overflow: hidden;
    color: var(--text-2);
    font-family: var(--font-doc);
    font-size: 13.5px;
    line-height: 1.35;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
  }

  .fixable {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    align-self: flex-start;
    color: var(--accent);
    font-size: 12px;
    font-weight: 580;
  }

  .wrong {
    display: inline-block;
    margin: 6px 0 2px 14px;
    color: var(--text-3);
    font-size: 12.5px;
  }

  .wrong:hover {
    color: var(--accent);
  }

  .more {
    margin-top: 8px;
    padding: 6px 10px;
    border: 0;
    border-radius: 8px;
    background: var(--surface-3);
    color: var(--text-2);
    font-size: 13px;
    cursor: pointer;
  }

  .passed {
    margin-top: 20px;
    padding: 10px 14px;
    border: 1px dashed var(--border-strong);
    border-radius: 12px;
    color: var(--text-2);
    font-size: 14px;
  }

  .passed summary {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--ok);
    font-weight: 580;
    cursor: pointer;
  }

  .passed ul {
    margin: 10px 0 4px;
    padding-left: 22px;
    columns: 2 200px;
    font-size: 13px;
  }

  .general {
    margin: 0 auto 16px;
    max-width: 693px;
    padding: 14px 16px;
    border: 1px solid var(--border);
    border-radius: var(--radius);
    background: var(--surface);
  }

  .general h3 {
    margin-bottom: 6px;
    color: var(--text-3);
    font-size: 12px;
    font-weight: 650;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .general ul {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .general-item {
    width: 100%;
    display: flex;
    align-items: baseline;
    gap: 10px;
    padding: 6px 4px;
    border: 0;
    border-radius: 8px;
    background: none;
    font-size: 14px;
    text-align: left;
    cursor: pointer;
  }

  .general-item:hover {
    background: var(--surface-2);
  }

  .general-item .dot {
    transform: translateY(-1px);
  }

  @media (max-width: 1100px) {
    .summary {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .tile-fix {
      grid-column: span 2;
    }
  }

  @media (max-width: 900px) {
    .report {
      padding: 20px 16px 16px;
    }

    .actions {
      width: 100%;
    }

    .actions .btn {
      flex: 1;
    }

    .num {
      font-size: 24px;
    }

    .tabs {
      display: flex;
      gap: 4px;
      margin-top: 16px;
      padding: 4px;
      border-radius: 12px;
      background: var(--surface-3);
    }

    .tabs button {
      flex: 1;
      height: 36px;
      border: 0;
      border-radius: 9px;
      background: none;
      color: var(--text-2);
      font-weight: 580;
      cursor: pointer;
    }

    .tabs button.active {
      background: var(--surface);
      color: var(--text);
      box-shadow: var(--shadow-sm);
    }

    .columns {
      grid-template-columns: minmax(0, 1fr);
      margin-top: 14px;
    }

    .issues {
      position: static;
      max-height: none;
      overflow: visible;
      padding: 0;
    }

    .hide {
      display: none;
    }
  }

  @media (max-width: 560px) {
    .wide-only,
    .full {
      display: none;
    }

    .short {
      display: inline;
    }

    .summary {
      gap: 8px;
    }

    .tile {
      padding: 10px 12px;
    }

    .lbl {
      font-size: 12px;
    }
  }
</style>
